/*
 * The four request patterns the console is built from, on top of http.request:
 *   ajaxSwap  -- fetch HTML and place it in a container
 *   ajaxHtml  -- fetch HTML and hand it to the caller
 *   ajaxJson  -- send and branch on the { success, modelState } envelope
 *   ajaxModal -- fetch HTML and show it in a modal
 * Option names and callback shapes are those of the jQuery originals, so an
 * un-migrated area script calling through the compat facade sees no change.
 */
import { toggleBusy, swapHtml, setSpinner, one, type Target, type SwapMode } from './dom';
import { setAlertMessage } from './alert';
import { toast } from './toast';
import { showModal, showModalError } from './modal';
import { applyUtcToInputs } from './forms';
import { setValidationModelState } from './validation';
import {
    request, handleStepUp, isJsonEnvelope, modelStateSummary, xhrFromError, statusFromError,
    type RequestData, type XhrLike, type JsonEnvelope,
} from './http';

/** The legacy (xhr, textStatus, errorThrown) error callback; return true to suppress the default alert. */
export type ErrorCallback = (xhr: XhrLike, status: string, error: string) => boolean | void;

interface CommonOptions {
    url: string;
    method?: string;
    data?: RequestData;
    contentType?: string | false;
    /** Element(s) to un-hide around the request (a spinner). */
    busy?: Target;
    onError?: ErrorCallback;
    onComplete?: () => void;
}

export interface SwapOptions extends CommonOptions {
    container: string | Element;
    mode?: SwapMode;
    /** Replace the container with a spinner while loading. */
    spinnerInto?: boolean;
    /** Where the failure alert goes; defaults to the container. */
    alertInto?: Target;
    alertTitle?: string;
    onSuccess?: (html: string) => void;
}

export interface HtmlOptions extends CommonOptions {
    alertTitle?: string;
    onSuccess?: (html: string) => void;
}

export interface JsonOptions extends CommonOptions {
    onSuccess?: (data: JsonEnvelope) => void;
    /** Return true to suppress the default failure rendering. */
    onFailure?: (data: unknown) => boolean | void;
    alertInto?: Target;
    errorTitle?: string;
    /** When set and the failure carries modelState, render it onto the form instead of an alert. */
    validationContainer?: string;
}

export interface ModalOptions extends CommonOptions {
    /** Modal element id (without #). */
    modal: string;
    /** Title text; omit/undefined to keep the container's existing header markup. */
    title?: string | null;
    footer?: string;
    formId?: string;
    focusId?: string;
    errorTitle?: string;
    errorMessage?: string;
    onSuccess?: (html: string) => void;
}

function errorMessage(error: unknown): string {
    const xhr = xhrFromError(error);
    return xhr.statusText || (error instanceof Error ? error.message : String(error));
}

/** Runs the caller's onError (if any) and reports whether the default UI should be suppressed. */
function reportError(error: unknown, onError?: ErrorCallback): { suppress: boolean; xhr: XhrLike; text: string } {
    const xhr = xhrFromError(error);
    const text = errorMessage(error);
    if (handleStepUp(xhr)) return { suppress: true, xhr, text };
    const suppress = onError ? onError(xhr, statusFromError(error), text) === true : false;
    return { suppress, xhr, text };
}

export async function ajaxSwap(opts: SwapOptions): Promise<void> {
    const alertInto: Target = opts.alertInto ?? opts.container;
    try {
        if (opts.spinnerInto) setSpinner(opts.container);
        toggleBusy(opts.busy, true);
        const result = await request(opts.url, { method: opts.method, data: opts.data, contentType: opts.contentType, accept: 'html' });
        swapHtml(opts.container, result.text, opts.mode ?? 'replace');
        applyUtcToInputs(opts.mode === 'replaceWith' ? undefined : opts.container);
        opts.onSuccess?.(result.text);
    } catch (error) {
        const { suppress, text } = reportError(error, opts.onError);
        if (!suppress) setAlertMessage(alertInto, 'danger', opts.alertTitle ?? 'Request failed', text);
    } finally {
        toggleBusy(opts.busy, false);
        opts.onComplete?.();
    }
}

export async function ajaxHtml(opts: HtmlOptions): Promise<string | null> {
    try {
        toggleBusy(opts.busy, true);
        const result = await request(opts.url, { method: opts.method, data: opts.data, contentType: opts.contentType, accept: 'html' });
        opts.onSuccess?.(result.text);
        return result.text;
    } catch (error) {
        const { suppress, text } = reportError(error, opts.onError);
        if (!suppress) toast('danger', opts.alertTitle ?? 'Request failed', text);
        return null;
    } finally {
        toggleBusy(opts.busy, false);
        opts.onComplete?.();
    }
}

export async function ajaxJson(opts: JsonOptions): Promise<JsonEnvelope | null> {
    const errorTitle = opts.errorTitle ?? 'Action failed';
    try {
        toggleBusy(opts.busy, true);
        const result = await request(opts.url, { method: opts.method, data: opts.data, contentType: opts.contentType, accept: 'json' });
        let data: unknown = result.json;
        if (data === undefined && result.text) {
            try { data = JSON.parse(result.text); } catch { data = undefined; }
        }
        if (isJsonEnvelope(data) && data.success) {
            opts.onSuccess?.(data);
            return data;
        }
        if (opts.onFailure?.(data) === true) return isJsonEnvelope(data) ? data : null;
        const modelState = isJsonEnvelope(data) ? data.modelState : undefined;
        if (opts.validationContainer && modelState) {
            setValidationModelState(modelState, opts.validationContainer);
        } else if (opts.alertInto) {
            setAlertMessage(opts.alertInto, 'danger', errorTitle, modelState ? modelStateSummary(modelState) : '');
        }
        return isJsonEnvelope(data) ? data : null;
    } catch (error) {
        const { suppress, text } = reportError(error, opts.onError);
        if (!suppress && opts.alertInto) setAlertMessage(opts.alertInto, 'danger', errorTitle, text);
        return null;
    } finally {
        toggleBusy(opts.busy, false);
        opts.onComplete?.();
    }
}

export async function ajaxModal(opts: ModalOptions): Promise<void> {
    const title = opts.title !== undefined ? opts.title : null;
    try {
        toggleBusy(opts.busy, true);
        const result = await request(opts.url, { method: opts.method, data: opts.data, contentType: opts.contentType, accept: 'html' });
        showModal(opts.modal, title, result.text, opts.footer ?? '', opts.formId, opts.focusId);
        opts.onSuccess?.(result.text);
    } catch (error) {
        const { suppress, text } = reportError(error, opts.onError);
        if (!suppress) showModalError(opts.modal, opts.errorTitle ?? opts.title ?? '', opts.errorMessage ?? 'Request failed:', text);
    } finally {
        toggleBusy(opts.busy, false);
        opts.onComplete?.();
    }
}

/**
 * The readable part of a failed write's { success, modelState } envelope.
 *
 * Every area needs this on every onFailure, and three of them had grown their
 * own identical copy. One definition, beside the request helpers that produce
 * the envelope in the first place.
 */
export function summaryOf(data: unknown): string {
    const modelState = (data as { modelState?: unknown } | null)?.modelState;
    return modelState ? modelStateSummary(modelState) : '';
}

/**
 * The common onFailure: report the refusal as a toast carrying its reason, and
 * return true so ajaxJson does not ALSO render an alert into the container.
 */
export function failToast(title: string): (data: unknown) => boolean {
    return data => {
        toast('danger', title, summaryOf(data));
        return true;
    };
}

/** Legacy one-liner: load a URL into a container with a spinner. */
export function loadContainer(containerId: string, url: string): Promise<void> {
    return ajaxSwap({ container: containerId, url, spinnerInto: true, alertTitle: 'Failed to load content' });
}

/** Re-fetch the whole dashboard partial into the view container. */
export function reloadDashboards(): Promise<void> {
    return ajaxSwap({ container: 'ViewContainer', url: 'Home/DashboardContent', alertTitle: 'Failed to reload dashboards' });
}

export { one };
